/**
 * Proving and relaying a Solana settlement (transfer, withdrawal, batch
 * withdrawal, migration).
 *
 * The browser rebuilds the source tree from finalized events, reads the pool
 * at finality, derives the action exactly as the relayer and the program
 * will re-derive it, proves in the isolated WASM worker, and posts the SDK
 * intent to the relayer through the proxy. The wallet never signs: the
 * relayer pays and is reimbursed from the proof-bound fee. The journal holds
 * the intent before it leaves the browser and the placed output notes after
 * finality, so any interruption reconciles from chain state.
 */
import {
  computeSolanaRecoveryRecordsDigest,
  bindRecoveryAdapterAux,
  type SolanaRecoveryRecordInput,
  SOLANA_TOKEN_PROGRAM_ID,
  SOLANA_ORCA_WHIRLPOOL_PROGRAM_ID,
  computeSolanaSwapAuxHash,
  solanaAdapterAddress160,
  solanaOrcaAdapterParams,
  solanaPrivateSwapAdapterParams,
  solanaPrivateSwapFeePolicyAddress,
  readSolanaSwapSnapshot,
  type SolanaSwapParams,
  buildSolanaSettlementIntent,
  computeSolanaWithdrawBatchHash,
  getSolanaAccounts,
  proveFlatJoinSplitV2,
  readSolanaPoolSnapshot,
  selectFlatJoinSplitV2Arity,
  solanaPoolAddresses,
  solanaRecipientAddress160,
  solanaSettleKindOf,
  type FlatJoinSplitV2ActionContext,
  type FlatJoinSplitV2Prover,
  type FlatJoinSplitV2ProveRequest,
  type SolanaPoolSnapshot,
  type SolanaSettlementIntent,
} from "@stealthos/sdk";
import { computeNullifier } from "../../joinsplit/keys";
import type {
  JoinSplitNote,
  SpendingKeys,
  StoredJoinSplitNote,
} from "../../joinsplit/types";
import type { SolanaNetworkConfig } from "../../config/solana";
import { flatFieldHex } from "../notes";
import { withFlatJoinSplitV2Prover } from "../prover";
import { progressLog } from "../settlement";
import { assertAscendingLeafIndices } from "../spendSelection";
import { inputWitness, outputWitness } from "../witness";
import { assertNotAborted, decimalScalar, fail } from "./common";
import { assertSolanaPoolServable, type SolanaDepositLog } from "./deposit";
import { assertSolanaRegisteredToken } from "../../config/solana";
import { finalizeSolanaSettlement, settlementDeliveries } from "./finalize";
import {
  prepareSolanaJournalRecord,
  solanaSettlementJournalId,
  updateSolanaJournalRecord,
  type SolanaJournalDelivery,
  type SolanaJournalRecord,
  type SolanaJournalScope,
} from "./journal";
import type { SolanaLeafCache } from "./leafCache";
import { scanSolanaTree } from "./leafDiscovery";
import { assertSolanaFlatNote } from "./notes";
import { SolanaRelayError, type SolanaRelayClient } from "./relay";
import {
  confirmSolanaSignature,
  SolanaTransactionFailedError,
  type SolanaRpc,
} from "./rpc";
import { solanaPoolAccountExists } from "./accounts";
import { formatInboxPublicKeyHex } from "../../utils/inbox-crypto";
import {
  prepareSolanaNoteRecovery,
  prepareSolanaSwapRecovery,
} from "./onchainRecovery";
import { ethers } from "ethers";

const ZERO_WORD = `0x${"00".repeat(32)}`;

export type SolanaSettlementKind =
  | "joinsplit"
  | "withdraw"
  | "withdraw_batch"
  | "swap"
  | "migrate";

export type SolanaProverRunner = <T>(
  callback: (prover: FlatJoinSplitV2Prover) => Promise<T>,
  options: { signal?: AbortSignal; onProgress?: (event: unknown) => void },
) => Promise<T>;

export interface SolanaSettlementContext {
  rpc: SolanaRpc;
  network: SolanaNetworkConfig;
  spendingKeys: SpendingKeys;
  journalScope: SolanaJournalScope;
  /** A matching durable ciphertext receipt is required before relay submission. */
  backupJournal?: (record: SolanaJournalRecord) => Promise<void>;
  /** Optional public capability refresh after proving, before exposing the proof. */
  beforeSubmit?: () => Promise<void>;
  relay: SolanaRelayClient;
  onLog: SolanaDepositLog;
  signal?: AbortSignal;
  cache?: SolanaLeafCache;
  /** Test seam; defaults to the isolated browser WASM prover. */
  proveWith?: SolanaProverRunner;
  pollMs?: number;
}

export interface SolanaSettlementPlan {
  kind: SolanaSettlementKind;
  mint: string | null;
  /** Ascending leaf indices, all in one tree, already authenticated once. */
  selected: StoredJoinSplitNote[];
  outputs: JoinSplitNote[];
  recipientOwner?: string;
  batch?: { owners: string[]; amounts: bigint[] };
  /** Gross amount leaving the pool (recipient amount for a withdrawal, total plus fee for a batch). */
  withdrawAmount?: bigint;
  fee?: bigint;
  swap?: SolanaSwapParams;
  swapSeed?: SolanaJournalRecord["swapSeed"];
  deliveries: SolanaJournalDelivery[];
}

export interface SolanaSettlementOutcome {
  signature: string;
  slot: number;
  outputs: JoinSplitNote[];
  journalId: string;
  replayed: boolean;
  intent: SolanaSettlementIntent;
}

export interface SolanaResolvedAction {
  actionContext: FlatJoinSplitV2ActionContext;
  actionTokenHash: string;
  outputTokenHash: string;
  outputTreeIndex: number;
}

/**
 * The action context the circuit commits to, derived exactly as the SDK's
 * verifier and the program derive it from an intent's public fields.
 */
function resolveSolanaAction(params: {
  kind: SolanaSettlementKind;
  snapshot: SolanaPoolSnapshot;
  recipientOwner?: string;
  batch?: { owners: string[]; amounts: bigint[] };
  fee?: bigint;
  swap?: SolanaSwapParams;
}): SolanaResolvedAction {
  const { kind, snapshot } = params;
  const paysOut =
    kind === "withdraw" || kind === "withdraw_batch" || kind === "swap";
  const sourceTree = Number(snapshot.rootTreeIndex);
  const currentTree = Number(snapshot.currentTreeIndex);
  const targetTree =
    kind === "joinsplit" ? sourceTree : kind === "migrate" ? currentTree : 0;
  const fee = paysOut ? (params.fee ?? 0n) : 0n;
  if (fee < 0n) fail("fee cannot be negative");
  let recipient = 0n;
  if (kind === "withdraw") {
    if (!params.recipientOwner) fail("a withdrawal needs a recipient");
    recipient = BigInt(solanaRecipientAddress160(params.recipientOwner));
  } else if (kind === "withdraw_batch") {
    if (!params.batch || params.batch.owners.length === 0) {
      fail("a batch withdrawal needs its payout list");
    }
    recipient = computeSolanaWithdrawBatchHash(
      params.batch.owners,
      params.batch.amounts,
    );
  }
  let aux = 0n;
  if (kind === "swap") {
    if (!params.swap) fail("swap terms are required");
    recipient = BigInt(
      solanaAdapterAddress160(SOLANA_ORCA_WHIRLPOOL_PROGRAM_ID),
    );
    aux = computeSolanaSwapAuxHash({
      ...params.swap,
      partnerId: params.swap.partnerId ?? (ZERO_WORD as `0x${string}`),
      adapterProgramId: SOLANA_ORCA_WHIRLPOOL_PROGRAM_ID,
      adapterParams: params.swap.privateFees
        ? solanaPrivateSwapAdapterParams(
            params.swap.whirlpool,
            params.swap.privateFees,
          )
        : solanaOrcaAdapterParams(params.swap.whirlpool),
    });
  }
  const feeRecipient = paysOut
    ? BigInt(solanaRecipientAddress160(snapshot.feeRecipientOwner))
    : 0n;
  if (paysOut && feeRecipient !== BigInt(snapshot.feeRecipient)) {
    fail("pool fee recipient projection differs from the SDK's");
  }
  return {
    actionContext: {
      chain_id: decimalScalar(snapshot.chainId),
      pool: decimalScalar(BigInt(snapshot.pool)),
      action_type: decimalScalar(solanaSettleKindOf(kind)),
      source_tree_index: decimalScalar(sourceTree),
      target_tree_index: decimalScalar(targetTree),
      token_address: decimalScalar(BigInt(snapshot.token)),
      recipient: decimalScalar(recipient),
      fee: decimalScalar(fee),
      fee_recipient: decimalScalar(feeRecipient),
      aux_data_hash: decimalScalar(aux),
    },
    actionTokenHash: snapshot.sourceTokenHash,
    outputTokenHash: kind === "migrate" ? snapshot.currentTokenHash : ZERO_WORD,
    // Withdrawals encode zero in the action, but append change to the source tree.
    outputTreeIndex: kind === "migrate" ? currentTree : sourceTree,
  };
}

export async function proveAndRelaySolanaSettlement(
  ctx: SolanaSettlementContext,
  plan: SolanaSettlementPlan,
): Promise<SolanaSettlementOutcome> {
  const { rpc, network, spendingKeys, onLog } = ctx;
  if (!network.enabled) fail(`${network.label} is not configured`);
  assertNotAborted(ctx.signal, "before witness construction");
  if (plan.selected.length === 0) fail("nothing to spend");
  const sourceTree = plan.selected[0].note.treeIndex;
  if (
    sourceTree === undefined ||
    plan.selected.some(({ note }) => note.treeIndex !== sourceTree)
  ) {
    fail("one proof cannot consume notes from multiple trees");
  }
  assertAscendingLeafIndices(plan.selected);
  const paysOut =
    plan.kind === "withdraw" ||
    plan.kind === "withdraw_batch" ||
    plan.kind === "swap";
  if (
    plan.kind === "swap" &&
    (!plan.swap || !plan.swapSeed || plan.selected.length > 13)
  )
    fail("swap needs backed-up output randomness and 1..13 inputs");
  const fee = paysOut ? (plan.fee ?? 0n) : 0n;
  if (
    paysOut &&
    plan.outputs.length >
      (plan.kind === "swap" && plan.swap?.privateFees ? 3 : 1)
  ) {
    fail("a withdrawal has at most one change note");
  }
  if (!paysOut && plan.outputs.length === 0) {
    fail("a transfer or migration produces at least one note");
  }

  onLog("Rebuilding the pool tree from finalized events", "info");
  const scan = await scanSolanaTree({
    rpc,
    network,
    treeIndex: sourceTree,
    cache: ctx.cache,
    signal: ctx.signal,
  });
  const snapshot = await readSolanaPoolSnapshot(rpc, {
    cluster: network.cluster,
    programId: network.programId,
    mint: plan.mint,
    treeIndex: sourceTree,
    root: scan.root as `0x${string}`,
    commitment: "finalized",
  });
  assertSolanaPoolServable(network, snapshot.poolConfig);
  assertSolanaRegisteredToken(network, plan.mint, snapshot.tokenConfig);
  if (plan.kind === "swap") {
    const swapSnapshot = await readSolanaSwapSnapshot(rpc, {
      cluster: network.cluster,
      programId: network.programId,
      mint: plan.mint,
      treeIndex: sourceTree,
      root: scan.root as `0x${string}`,
      outputMint: plan.swap!.outputMint,
      whirlpool: plan.swap!.whirlpool,
      commitment: "finalized",
    });
    const privateMode =
      swapSnapshot.swapConfig.treasury ===
      solanaPrivateSwapFeePolicyAddress(network.programId);
    if (
      privateMode !== Boolean(plan.swap!.privateFees) ||
      (privateMode && swapSnapshot.swapConfig.protocolFeeBps !== 0)
    )
      fail("Swap fee policy changed. Get a fresh quote.");
    const slot = await rpc.request<number>("getSlot", [
      { commitment: "confirmed" },
    ]);
    if (BigInt(slot) >= BigInt(plan.swap!.deadlineSlot))
      fail("swap quote expired");
  }
  if (plan.kind === "joinsplit" && !snapshot.rootIsCurrent) {
    fail("transfers spend from the current tree; migrate these notes first");
  }
  if (plan.kind === "migrate" && snapshot.rootIsCurrent) {
    fail("only notes in a retired tree can be migrated");
  }
  if (paysOut && plan.outputs.length > 0 && !snapshot.rootIsCurrent) {
    fail(
      "withdrawals with change require the current tree; migrate these notes first",
    );
  }
  if (!paysOut && !snapshot.tokenConfig.enabled) {
    fail("this token is disabled on the pool; only withdrawals are accepted");
  }
  const action = resolveSolanaAction({
    kind: plan.kind,
    snapshot,
    recipientOwner: plan.recipientOwner,
    batch: plan.batch,
    fee,
    swap: plan.swap,
  });
  const expectedOutputTokenHash = (
    plan.kind === "migrate"
      ? snapshot.currentTokenHash
      : snapshot.sourceTokenHash
  ).toLowerCase();
  for (const note of plan.outputs) {
    if (note.treeIndex !== action.outputTreeIndex) {
      fail("output note tree does not match the action");
    }
    if (flatFieldHex(note.tokenHash) !== expectedOutputTokenHash) {
      fail("output note token domain does not match the action");
    }
  }

  const addresses = solanaPoolAddresses(network.programId);
  for (const { note } of plan.selected) {
    await assertSolanaFlatNote({
      note,
      network,
      mint: plan.mint,
      expectedTokenHash: snapshot.sourceTokenHash,
      expectedMpk: spendingKeys.mpk,
    });
  }
  const nullifiers = await Promise.all(
    plan.selected.map(({ note }) =>
      computeNullifier(
        spendingKeys.nullifyingKey,
        note.tokenHash,
        note.leafIndex,
      ),
    ),
  );
  const { accounts } = await getSolanaAccounts(
    rpc,
    nullifiers.map((nullifier) =>
      addresses.nullifier(nullifier as `0x${string}`),
    ),
    "finalized",
  );
  accounts.forEach((info, index) => {
    if (solanaPoolAccountExists(info, network.programId, "nullifier"))
      fail(
        `note ${plan.selected[index].note.commitment} is already spent on chain`,
      );
  });
  const journalId = await solanaSettlementJournalId(
    network.key,
    addresses.programId,
    nullifiers,
  );

  // Bind ciphertext into the proof. The program requires the exact immutable
  // records when it settles, so no independent prepublication/finality wait is
  // necessary and a relayer cannot strip or substitute recovery data.
  const recoveryPublications = await Promise.all(
    (network.onchainRecoveryEnabled ? plan.outputs : []).map((note) => {
      const delivery = plan.deliveries.find(
        (d) => d.commitment.toLowerCase() === note.commitment.toLowerCase(),
      );
      const inboxKey =
        delivery?.recipientInboxPublicKeyHex ??
        (delivery?.keepLocal
          ? formatInboxPublicKeyHex(spendingKeys.spendingKey)
          : null);
      if (!inboxKey)
        fail("Output recipient has no on-chain recovery encryption key");
      return prepareSolanaNoteRecovery(network, note, inboxKey);
    }),
  );
  if (network.onchainRecoveryEnabled && plan.swapSeed)
    recoveryPublications.push(
      await prepareSolanaSwapRecovery(
        network,
        spendingKeys,
        nullifiers[0],
        plan.swapSeed,
      ),
    );
  const recoveryRecords: SolanaRecoveryRecordInput[] | undefined =
    network.onchainRecoveryEnabled
      ? recoveryPublications.map((r) => ({
          kind: r.kind as 1 | 2,
          locator: r.locator,
          payload: ethers.hexlify(r.payload) as `0x${string}`,
        }))
      : undefined;
  if (recoveryRecords !== undefined) {
    const digest = computeSolanaRecoveryRecordsDigest({
      cluster: network.cluster,
      programId: network.programId,
      commitments: plan.outputs.map((n) => n.commitment as `0x${string}`),
      swapFirstNullifier:
        plan.kind === "swap" ? (nullifiers[0] as `0x${string}`) : undefined,
      records: recoveryRecords,
    });
    action.actionContext.aux_data_hash = decimalScalar(
      plan.kind === "swap"
        ? BigInt(
            bindRecoveryAdapterAux(
              ethers.toBeHex(BigInt(action.actionContext.aux_data_hash), 32),
              digest,
            ),
          )
        : BigInt(digest),
    );
  }

  onLog(
    `Proving a ${plan.selected.length}-input, ${plan.outputs.length}-output settlement`,
    "info",
  );
  const proveWith =
    ctx.proveWith ?? (withFlatJoinSplitV2Prover as SolanaProverRunner);
  const intent = await proveWith(
    async (prover) => {
      const inputs = plan.selected.map(({ note }) =>
        inputWitness(note, scan.tree.path(note.leafIndex), spendingKeys),
      );
      const outputs = plan.outputs.map(outputWitness);
      const request: FlatJoinSplitV2ProveRequest = {
        arity_id: selectFlatJoinSplitV2Arity(inputs.length, outputs.length),
        merkle_root: scan.root as `0x${string}`,
        inputs,
        outputs,
        withdraw_amount: decimalScalar(plan.withdrawAmount ?? 0n),
        action_token_hash: action.actionTokenHash as `0x${string}`,
        output_token_hash: action.outputTokenHash as `0x${string}`,
        action_context: action.actionContext,
        spend_secret_scalar: decimalScalar(spendingKeys.spendSecretScalar),
      };
      const bundle = await proveFlatJoinSplitV2(prover, request, ctx.signal);
      return buildSolanaSettlementIntent({
        recoveryRecords,
        cluster: network.cluster,
        programId: addresses.programId,
        mint: plan.mint,
        // Native SOL has no token program; the intent carries the SPL Token
        // program the way the program's own vectors do.
        tokenProgram:
          plan.mint === null ? SOLANA_TOKEN_PROGRAM_ID : snapshot.tokenProgram,
        bundle,
        sourceTreeIndex: sourceTree,
        currentTreeIndex: snapshot.currentTreeIndex,
        feeRecipient: snapshot.feeRecipientOwner,
        ...(plan.kind === "withdraw"
          ? {
              recipientOwner: plan.recipientOwner,
              amount: plan.withdrawAmount,
              fee,
            }
          : {}),
        ...(plan.kind === "withdraw_batch" ? { batch: plan.batch, fee } : {}),
        ...(plan.kind === "swap"
          ? { swap: plan.swap, amount: plan.withdrawAmount, fee }
          : {}),
      });
    },
    {
      signal: ctx.signal,
      onProgress: progressLog(onLog) as (event: unknown) => void,
    },
  );
  // The worker is gone here; nothing below keeps witness material alive.
  assertNotAborted(ctx.signal, "after proving");

  const { record, resumed } = await prepareSolanaJournalRecord(
    ctx.journalScope,
    {
      id: journalId,
      kind: plan.kind,
      intent,
      sourceCommitments: plan.selected.map(({ note }) => note.commitment),
      sourceNullifiers: nullifiers,
      outputNotes: plan.outputs,
      deliveries: plan.deliveries,
      ...(plan.swapSeed ? { swapSeed: plan.swapSeed } : {}),
    },
  );
  if (resumed) {
    fail(
      "a settlement for these notes is already in flight; refresh to reconcile it before retrying",
    );
  }

  try {
    await ctx.beforeSubmit?.();
    assertNotAborted(ctx.signal, "before recovery backup");
    // Hosted backups are optional convenience once immutable chain recovery is
    // finalized. Their outage must not block spending independently recovered notes.
    if (!network.onchainRecoveryEnabled) {
      if (!ctx.backupJournal)
        fail("encrypted Solana recovery backup is required before submission");
      await ctx.backupJournal(record);
    }
    assertNotAborted(ctx.signal, "after recovery backup");
  } catch (error) {
    // No relayer call was made. A fresh proof may safely replace this record.
    await updateSolanaJournalRecord(
      ctx.journalScope,
      record.id,
      {
        state: "failed",
        failureConfirmed: true,
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }
  await updateSolanaJournalRecord(
    ctx.journalScope,
    record.id,
    {
      submissionAttempted: true,
    },
    record,
  );

  onLog("Submitting to the relayer", "info");
  let response;
  try {
    response = await ctx.relay.settle(intent, {
      idempotencyKey: record.id,
      signal: ctx.signal,
    });
  } catch (error) {
    // A lost HTTP response does not prove that the relayer refused the intent.
    await updateSolanaJournalRecord(
      ctx.journalScope,
      record.id,
      {
        ...(error instanceof SolanaRelayError &&
        (error.submissionNotAttempted || error.settlementNotSubmitted)
          ? { state: "failed" as const, failureConfirmed: true }
          : {}),
        error: error instanceof Error ? error.message : String(error),
      },
      record,
    );
    throw error;
  }
  await updateSolanaJournalRecord(
    ctx.journalScope,
    record.id,
    {
      state: "broadcast",
      signature: response.settleSignature,
    },
    record,
  );
  onLog(
    response.replayed
      ? "Relayer had already settled these notes; confirming"
      : "Settlement submitted; waiting for finality",
    "info",
    response.settleSignature,
  );

  try {
    await confirmSolanaSignature(rpc, {
      signature: response.settleSignature,
      commitment: "finalized",
      pollMs: ctx.pollMs,
      signal: ctx.signal,
    });
  } catch (error) {
    if (!ctx.signal?.aborted) {
      await updateSolanaJournalRecord(
        ctx.journalScope,
        record.id,
        {
          ...(error instanceof SolanaTransactionFailedError
            ? { state: "failed" as const, failureConfirmed: true }
            : {}),
          error: error instanceof Error ? error.message : String(error),
        },
        record,
      );
    }
    throw error;
  }
  const finalized = await finalizeSolanaSettlement(rpc, network, {
    signature: response.settleSignature,
    nullifiers: intent.nullifiers,
    commitments: intent.commitments,
    outputs: plan.outputs,
    expectedIntent: intent,
    swapSeed: plan.swapSeed,
  });
  await updateSolanaJournalRecord(
    ctx.journalScope,
    record.id,
    {
      state: "finalized",
      slot: finalized.slot,
      outputNotes: finalized.outputs,
      deliveries: settlementDeliveries(record, finalized.outputs),
    },
    record,
  );
  onLog("Settlement finalized", "success", response.settleSignature);
  return {
    signature: response.settleSignature,
    slot: finalized.slot,
    outputs: finalized.outputs,
    journalId: record.id,
    replayed: Boolean(response.replayed),
    intent,
  };
}
